/* Main mobile swipes use the carousel's public one-step API, not its fling calculation. */
(() => {
  const mobile = window.matchMedia('(max-width: 767px)');
  let gesture = null;
  let suppressClick = null;

  function mainViewport(target) {
    if (!(target instanceof Element) || !mobile.matches) return null;
    const inner = target.closest('.nps-product-gallery-mobile__main');
    if (!inner || target.closest('model-viewer, video, button, input, select, textarea, [contenteditable="true"]')) return null;
    const carousel = inner.parentElement;
    if (!carousel || carousel.localName !== 'xo-carousel' || !carousel.closest('.nps-product-gallery-mobile')) return null;
    return { inner, carousel };
  }

  document.addEventListener('pointerdown', (event) => {
    if (!event.isPrimary) {
      gesture = null;
      return;
    }
    if (event.button !== 0) return;
    const viewport = mainViewport(event.target);
    if (!viewport || typeof window.xoCarousel?.next !== 'function' || !viewport.carousel.xoName) return;
    gesture = {
      ...viewport,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      axis: null
    };
  }, { capture: true, passive: true });

  function preventParallelNativeDrag(event) {
    const viewport = mainViewport(event.target);
    if (gesture && viewport?.inner === gesture.inner) event.stopPropagation();
  }

  // The native pan helper starts on these bubbling events; keep it from sharing this gesture.
  document.addEventListener('mousedown', preventParallelNativeDrag, { capture: true, passive: true });
  document.addEventListener('touchstart', preventParallelNativeDrag, { capture: true, passive: true });

  document.addEventListener('pointermove', (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 12) {
      if (Math.abs(dx) > Math.abs(dy) * 1.25) {
        gesture.axis = 'horizontal';
      } else {
        gesture = null;
        return;
      }
    }
    if (gesture?.axis === 'horizontal' && event.cancelable) event.preventDefault();
  }, { capture: true, passive: false });

  document.addEventListener('pointerup', (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const completed = gesture;
    gesture = null;
    const dx = event.clientX - completed.startX;
    const dy = event.clientY - completed.startY;
    const threshold = Math.min(48, Math.max(24, completed.inner.clientWidth * 0.08));
    if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
    if (!completed.carousel.isConnected || !mobile.matches) return;
    const api = window.xoCarousel;
    if (typeof api?.next !== 'function' || typeof api?.prev !== 'function') return;
    const rtl = (completed.carousel.closest('[dir]')?.getAttribute('dir') || document.documentElement.dir) === 'rtl';
    const forward = rtl ? dx > 0 : dx < 0;
    if (event.cancelable) event.preventDefault();
    suppressClick = { carousel: completed.carousel, until: performance.now() + 500 };
    api[forward ? 'next' : 'prev'](completed.carousel.xoName, 1);
  }, { capture: true, passive: false });

  document.addEventListener('pointercancel', (event) => {
    if (gesture?.pointerId === event.pointerId) gesture = null;
  }, { capture: true, passive: true });

  document.addEventListener('dragstart', (event) => {
    if (mainViewport(event.target)) event.preventDefault();
  }, { capture: true });

  // Select dots immediately through the public API; the native click handler has a shared cooldown.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !mobile.matches) return;
    const bullet = event.target.closest('.nps-product-gallery-mobile__pagination xo-carousel-bullet');
    const carousel = bullet?.closest('xo-carousel');
    const index = Number(bullet?.getAttribute('xo-index'));
    if (!bullet || !carousel?.xoName || !Number.isInteger(index) || typeof window.xoCarousel?.goTo !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    window.xoCarousel.goTo(carousel.xoName, index);
  }, { capture: true });

  document.addEventListener('click', (event) => {
    const viewport = mainViewport(event.target);
    if (!suppressClick || !viewport || viewport.carousel !== suppressClick.carousel || performance.now() > suppressClick.until) return;
    suppressClick = null;
    event.preventDefault();
    event.stopPropagation();
  }, { capture: true });
})();

/* Reveal controls when the first image and native carousel are ready; retain selection on native re-init. */
(() => {
  const gallerySelector = '.nps-product-gallery-mobile';
  const states = new WeakMap();

  function imageKey(image) {
    return image ? `${image.getAttribute('src') || ''}|${image.getAttribute('srcset') || ''}` : '';
  }

  function updateReady(state) {
    const initialized = state.carousel.hasAttribute('xo-initialized') && state.carousel.getAttribute('xo-initialized') !== 'false';
    if (!initialized || !state.imageSettled) {
      if (state.readyFrame !== null) cancelAnimationFrame(state.readyFrame);
      state.readyFrame = null;
      state.gallery.removeAttribute('data-nps-gallery-ready');
      return;
    }
    if (state.gallery.hasAttribute('data-nps-gallery-ready') || state.readyFrame !== null) return;
    state.readyFrame = requestAnimationFrame(() => {
      state.readyFrame = null;
      if (state.carousel.isConnected && state.imageSettled && state.carousel.hasAttribute('xo-initialized') && state.carousel.getAttribute('xo-initialized') !== 'false') {
        state.gallery.setAttribute('data-nps-gallery-ready', '');
      }
    });
  }

  function settleImage(state) {
    if (state.imageSettled || state.decodePending) return;
    const image = state.image;
    const revision = state.imageRevision;
    if (!image || image.naturalWidth === 0 || typeof image.decode !== 'function') {
      state.imageSettled = true;
      updateReady(state);
      return;
    }
    state.decodePending = true;
    Promise.resolve().then(() => image.decode()).catch(() => {}).then(() => {
      if (revision !== state.imageRevision || state.image !== image) return;
      state.decodePending = false;
      state.imageSettled = true;
      updateReady(state);
    });
  }

  function refreshImage(state) {
    const image = state.carousel.querySelector('.nps-product-gallery-mobile__main > xo-carousel-list > :first-child img');
    const key = imageKey(image);
    if (image !== state.image || key !== state.imageSource || (image && !image.complete && state.imageSettled)) {
      if (state.image && state.onImageSettled) {
        state.image.removeEventListener('load', state.onImageSettled);
        state.image.removeEventListener('error', state.onImageSettled);
      }
      state.image = image;
      state.imageSource = key;
      state.imageSettled = !image;
      state.decodePending = false;
      ++state.imageRevision;
      const revision = state.imageRevision;
      state.onImageSettled = () => {
        if (state.image !== image || revision !== state.imageRevision) return;
        settleImage(state);
      };
      if (image && !image.complete) {
        image.addEventListener('load', state.onImageSettled, { once: true });
        image.addEventListener('error', state.onImageSettled, { once: true });
        // Cached completion can occur between the first check and listener registration.
        if (image.complete) settleImage(state);
      } else if (image) {
        settleImage(state);
      }
    } else if (image?.complete) {
      settleImage(state);
    }
    updateReady(state);
  }

  function installGallery(gallery) {
    const carousel = gallery.querySelector(':scope > xo-carousel');
    if (!carousel) return;
    let state = states.get(gallery);
    if (!state || state.carousel !== carousel) {
      if (state) {
        state.carousel.removeEventListener('change', state.onChange);
        state.carousel.removeEventListener('xo:carousel:init', state.onInit);
        if (state.image && state.onImageSettled) {
          state.image.removeEventListener('load', state.onImageSettled);
          state.image.removeEventListener('error', state.onImageSettled);
        }
      }
      state = { gallery, carousel, image: null, imageSource: null, imageSettled: false, decodePending: false, imageRevision: 0, readyFrame: null, lastSelection: null, selectionRevision: 0 };
      state.onChange = (event) => {
        if (event.target !== carousel) return;
        const index = event.detail?.activeIndex;
        if (!Number.isInteger(index) || index < 0) return;
        const revision = ++state.selectionRevision;
        queueMicrotask(() => {
          if (revision === state.selectionRevision && carousel.isConnected) state.lastSelection = index;
        });
      };
      state.onInit = (event) => {
        if (event.target !== carousel) return;
        // Native init follows its reset change synchronously, before that change can commit.
        ++state.selectionRevision;
        if (state.lastSelection !== null && carousel.isConnected && typeof window.xoCarousel?.goTo === 'function' && carousel.xoName) {
          window.xoCarousel.goTo(carousel.xoName, state.lastSelection);
        }
        refreshImage(state);
      };
      carousel.addEventListener('change', state.onChange);
      carousel.addEventListener('xo:carousel:init', state.onInit);
      states.set(gallery, state);
    }
    refreshImage(state);
  }

  function collectGalleries(node, found) {
    if (!(node instanceof Element)) return;
    const parentGallery = node.closest(gallerySelector);
    if (parentGallery) found.add(parentGallery);
    node.querySelectorAll(gallerySelector).forEach((gallery) => found.add(gallery));
  }

  document.querySelectorAll(gallerySelector).forEach(installGallery);
  const observer = new MutationObserver((records) => {
    const found = new Set();
    records.forEach((record) => {
      collectGalleries(record.target, found);
      record.addedNodes.forEach((node) => collectGalleries(node, found));
    });
    found.forEach((gallery) => {
      if (gallery.isConnected) installGallery(gallery);
    });
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['xo-initialized', 'src', 'srcset']
  });
})();
